import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { clearSelectedMedia } from '@ports/common/commonStateSlice';
import MediaPlayer from '@business/workflows/ComponentUI/MediaPlayer';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { SVGAssets } from '@business/assets/vectors';
import {
  useGetCountsQuery,
  useGetLikesQuery,
  useAddLikesMutation,
  useDeleteLikesMutation
} from '@ports/internal-services/likes/likesSlice';
import {
  useGetCommentsQuery,
  useAddCommentsMutation,
  useUpdateCommentsMutation,
  useDeleteCommentsMutation
} from '@ports/internal-services/comment/commentSlice';
import {
  useDeleteAssetsMutation,
  useGetAssetsByIdQuery,
  useGetAssetsPeopleQuery
} from '@ports/internal-services/asset/assetSlice';
import { useAddFavoritesMutation } from '@ports/internal-services/favorites/favoritesSlice';
import { getUserData } from '@ports/common/userStateSlice';
import { getSubscriberId, getToken } from '@ports/common/sessionStateSlice';
import { getPermissions } from '@ports/common/authStateSlice';
import {
  Comment,
  MediaPostScreenProps,
  MediaType
} from '@business/interfaces/photo';
import { AppLoader, ConfirmationModal } from '@business/workflows/ComponentUI';
import { handleDownloads } from '@utils/helpers/lib/photoHelpers';
import { routePaths } from '@utils/constants/routePaths';
import { SelectFriendFamily } from '@business/workflows/ComponentUI/People/SelectFriendFamily';
import { SearchableSelectInput } from '@business/workflows/ComponentUI/SearchableInput';
import { Button } from '@business/workflows/ComponentUI';
import {
  useAddPeopleAssetMutation,
  useAddPeopleMutation,
  useGetPeopleByNameQuery,
  useUpdatePeopleMutation
} from '@ports/internal-services/people/peopleSlice';
import { useDebounce } from 'src/hooks/Debounce';
import { FriendsForm } from '@business/workflows/ComponentUI/People/FriendsForm';
import { FamilyForm } from '@business/workflows/ComponentUI/People/FamilyForm';
import { MergePerson } from '@business/workflows/ComponentUI/People';
import { useMergePeopleMutation } from '@ports/internal-services/people/peopleSlice';
import { useTranslation } from 'react-i18next';
import { showToast } from '@business/workflows/ComponentUI/Toast/Toast';
import { extractFaceFromImage } from '@utils/helpers/lib/photoHelpers/extractFaceFromImage';
import { uploadToS3 } from '@utils/helpers/s3Upload';
import { ShareModalComponent } from '@business/workflows/ComponentUI/Share';
import DiarySection from './DiarySection';
import MediaInfoSidebar from './MediaInfoSidebar';
import FullScreenModal from './FullScreenModal';
import CommentSection from './CommentSection';
import MediaStats from './MediaStats';
import MediaPostHeader from './MediaPostHeader';

const MediaPostScreen: React.FC<MediaPostScreenProps> = ({ assetId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userDetails = useSelector(getUserData);
  const userId = userDetails.userId || ''; // Using email as userId, adjust as needed
  const subscriberId = useSelector(getSubscriberId);
  const idToken = useSelector(getToken);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const permissions = useSelector(getPermissions);
  const canShare = permissions.includes('sharing:update');
  const canDelete = permissions.includes('photo:delete');
  const canDownload = permissions.includes('photo:download');

  // Fetch asset data
  const { data: assetData, refetch: refetchAsset } = useGetAssetsByIdQuery(
    { id: assetId },
    { skip: !assetId }
  );

  // Fetch counts (likes, comments, diary)
  const { data: countsData, refetch: refetchCounts } = useGetCountsQuery(
    { assetId },
    { skip: !assetId }
  );

  // Fetch user's like status
  const { data: likesData, refetch: refetchLikes } = useGetLikesQuery(
    { assetId },
    { skip: !assetId }
  );

  // Fetch comments
  const { data: commentsData, refetch: refetchComments } = useGetCommentsQuery(
    { assetId },
    { skip: !assetId }
  );

  const { data: assetPeopleData, refetch: refetchAssetPeople } =
    useGetAssetsPeopleQuery({ id: assetId }, { skip: !assetId });

  // Mutations
  const [addLike] = useAddLikesMutation();
  const [deleteLike] = useDeleteLikesMutation();
  const [addComment] = useAddCommentsMutation();
  const [updateComment] = useUpdateCommentsMutation();
  const [deleteComment] = useDeleteCommentsMutation();
  const [deleteAsset] = useDeleteAssetsMutation();
  const [addFavorite] = useAddFavoritesMutation();

  // Local state for optimistic updates
  const [isLiked, setIsLiked] = useState(false);
  const [userLikeId, setUserLikeId] = useState<string | null>(null);
  const [optimisticLikeCount, setOptimisticLikeCount] = useState(0);
  const [optimisticCommentCount, setOptimisticCommentCount] = useState(0);
  const [optimisticDiaryCount, setOptimisticDiaryCount] = useState(0);
  const [optimisticComments, setOptimisticComments] = useState<Comment[]>([]);
  const [isProcessingLike, setIsProcessingLike] = useState<boolean>(false);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [isInfoSidebarOpen, setIsInfoSidebarOpen] = useState<boolean>(false);
  const [isDiarySectionOpen, setIsDiarySectionOpen] = useState<boolean>(false);
  const [isCommentSectionOpen, setIsCommentSectionOpen] =
    useState<boolean>(true);
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [isFavorite, setIsFavorite] = useState<boolean>(false);

  const [imageDimensions, setImageDimensions] = useState<any>(null);

  // Who is this feature state
  const [activeFaceIndex, setActiveFaceIndex] = useState<number | null>(null);
  const [hoveredFaceIndex, setHoveredFaceIndex] = useState<number | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [isNameSelected, setIsNameSelected] = useState<boolean>(false);
  const [selectedPersonId, setSelectedPersonId] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isSelectFriendFamily, setIsSelectFriendFamily] =
    useState<boolean>(false);
  const [selectedPersonType, setSelectedPersonType] = useState<
    'family' | 'friend' | null
  >(null);
  const [isOpenFriendsForm, setIsOpenFriendsForm] = useState<boolean>(false);
  const [isOpenFamilyForm, setIsOpenFamilyForm] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [ownerId] = useState<string>('');
  const [createdPersonId, setCreatedPersonId] = useState<string>('');

  // Merge person modal state
  const [isMergePersonOpen, setIsMergePersonOpen] = useState<boolean>(false);
  const [sourcePersonIdForMerge, setSourcePersonIdForMerge] =
    useState<string>('');
  const [selectedMergePersonId, setSelectedMergePersonId] =
    useState<string>('');
  const [selectedMergePerson, setSelectedMergePerson] = useState<string>('');
  const [newPersonImage, setNewPersonImage] = useState<string>('');
  const [selectedPersonImage, setSelectedPersonImage] = useState<string>('');

  // Ref for face popup to handle outside clicks
  const facePopupRef = useRef<HTMLDivElement>(null);

  // People mutations
  const [MergeMutation] = useMergePeopleMutation();

  // Debounce search for people
  const debouncedSearch = useDebounce(searchText, 400);
  const { data: peopleDataForSearch } = useGetPeopleByNameQuery(
    { search: debouncedSearch },
    { skip: !debouncedSearch }
  );

  useEffect(() => {
    refetchAsset();
    refetchAssetPeople();
  }, []);

  const familySearchOptions = useMemo(() => {
    return (
      peopleDataForSearch?.data?.rows?.map((item: any) => ({
        id: item?.id,
        name: item?.name,
        profilePicUrl: item?.profileImage,
        userId: item?.userId,
        subscriberId: item?.subscriberId
      })) || []
    );
  }, [peopleDataForSearch?.data?.rows]);

  // Extract media data from asset
  const mediaType = assetData?.data?.contentType || 'image';
  const mediaSrc = assetData?.data?.url || '';
  // Face bounding boxes (array of { boundingBox: { left, top, width, height } })
  const faceDetections = assetData?.data?.faceDetections || [];

  // Determine if the asset is a PDF
  const isPdf = mediaType === 'pdf' || mediaSrc.toLowerCase().endsWith('.pdf');

  const isAudio =
    mediaType?.startsWith('audio/') ||
    mediaSrc.toLowerCase().match(/\.(mp3|wav|ogg|aac)$/);

  const mergedFaceDetections = useMemo(() => {
    if (!faceDetections?.length || !assetPeopleData?.data?.length) {
      return faceDetections;
    }

    // Create quick lookup map using userId
    const peopleMap = new Map(
      assetPeopleData.data.map((person: any) => [person.userId, person])
    );

    return faceDetections.map((face: any) => {
      const matchedPerson = peopleMap.get(face.userId);

      return {
        ...face,
        peopleId: matchedPerson?.id || null,
        personName: matchedPerson?.name || null,
        profileImage: matchedPerson?.profileImage || null,
        personUserId: matchedPerson?.userId || null,
        personSubscriberId: matchedPerson?.subscriberId || null
      };
    });
  }, [faceDetections, assetPeopleData]);

  // Update favorite status when asset data is fetched
  useEffect(() => {
    if (assetData?.data?.favorite) {
      setIsFavorite(assetData?.data?.favorite?.status === 'Active');
    }
  }, [assetData]);

  // Update like status when data is fetched
  useEffect(() => {
    if (likesData?.data) {
      setIsLiked(likesData.data.count ? likesData.data.count > 0 : false);
    }
  }, [likesData]);

  // Update optimistic counts when real data arrives
  useEffect(() => {
    if (countsData?.data) {
      setOptimisticLikeCount(countsData.data.likes || 0);
      setOptimisticCommentCount(countsData.data.comments || 0);
      setOptimisticDiaryCount(countsData.data.diary || 0);
    }
  }, [countsData]);

  // Transform comments data to match Comment interface
  const transformedComments: Comment[] =
    commentsData?.data?.rows?.map(comment => ({
      id: comment.id,
      name: comment.name,
      profileImage: comment.profileImage,
      text: comment.comment,
      timestamp: comment.createdAt,
      userId: comment.userId,
      commentLikesCount: comment.commentLikesCount || 0,
      commentLikeCount: comment.commentLikesCount || 0,
      commentLikes: comment.commentLikes || [],
      isLiked: false,
      userCommentLikeId: null as string | null
    })) || [];

  // Update comments when data is fetched
  useEffect(() => {
    setOptimisticComments(transformedComments);
  }, [commentsData]);

  const displayComments = optimisticComments;

  const handleCommentLikeUpdate = () => {
    // Just refetch comments to get updated like counts
    // The child component already handles the API call and optimistic updates
    setTimeout(() => {
      refetchComments();
    }, 100);
  };

  const handleDiaryUpdate = () => {
    // Refetch counts when diary is added/deleted
    setTimeout(() => {
      refetchCounts();
    }, 100);
  };

  const handleFavorite = async () => {
    const previousIsFavorite = isFavorite;

    try {
      // Optimistic update
      setIsFavorite(!isFavorite);

      // Call API
      const status = !isFavorite ? 'Active' : 'InActive';
      await addFavorite({
        createFavorites: {
          userId,
          assetId,
          subscriberId,
          status
        }
      }).then(() => {
        refetchAsset();
      });
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      // Rollback on failure
      setIsFavorite(previousIsFavorite);
    }
  };

  const handleLike = async () => {
    // Prevent multiple rapid clicks
    if (isProcessingLike) return;
    setIsProcessingLike(true);

    // Store previous state for rollback
    const previousIsLiked = isLiked;
    const previousCount = optimisticLikeCount;
    const previousLikeId = userLikeId;

    try {
      // 1. OPTIMISTIC UPDATE - Update UI immediately
      setIsLiked(!isLiked);
      setOptimisticLikeCount(prev => (isLiked ? prev - 1 : prev + 1));

      if (isLiked && userLikeId) {
        // Unlike
        await deleteLike({ id: userLikeId, userId, assetId }).unwrap();
        setUserLikeId(null);
      } else {
        // Like
        const result = await addLike({
          createLikes: { assetId, userId }
        }).unwrap();
        if (result.data?.id) {
          setUserLikeId(result.data.id);
        }
      }

      // 2. BACKGROUND REFRESH - Fetch real data without blocking UI
      setTimeout(() => {
        refetchCounts();
        refetchLikes();
      }, 100);
    } catch (error) {
      console.error('Failed to toggle like:', error);

      // 3. ROLLBACK on failure
      setIsLiked(previousIsLiked);
      setOptimisticLikeCount(previousCount);
      setUserLikeId(previousLikeId);
    } finally {
      // Allow next click after a short delay
      setTimeout(() => setIsProcessingLike(false), 300);
    }
  };

  const handleAddComment = async (text: string) => {
    try {
      // API call first, wait for success
      await addComment({
        createComments: {
          userId,
          assetId,
          comment: text
        }
      }).unwrap();

      // Refetch comments after successful API call
      refetchComments();
      refetchCounts();
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleUpdateComment = async (commentId: string, text: string) => {
    try {
      await updateComment({
        id: commentId,
        updateComments: {
          comment: text
        }
      }).unwrap();

      // Refetch comments after successful update
      refetchComments();
    } catch (error) {
      console.error('Failed to update comment:', error);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    // Store previous state for rollback
    const previousComments = [...optimisticComments];
    const previousCount = optimisticCommentCount;

    try {
      // 1. OPTIMISTIC UPDATE - Remove comment immediately
      setOptimisticComments(prev =>
        prev.filter(comment => comment.id !== commentId)
      );
      setOptimisticCommentCount(prev => prev - 1);

      // 2. API CALL
      await deleteComment({ id: commentId, delete: 'true' }).unwrap();

      // 3. BACKGROUND REFRESH
      setTimeout(() => {
        refetchComments();
        refetchCounts();
      }, 100);
    } catch (error) {
      console.error('Failed to delete comment:', error);

      // ROLLBACK - Restore deleted comment
      setOptimisticComments(previousComments);
      setOptimisticCommentCount(previousCount);
    }
  };

  const handleBack = () => {
    dispatch(clearSelectedMedia());
    navigate(-1);
  };

  const handleExpand = () => {
    setIsFullScreenOpen(true);
  };

  const handleZoomIn = () => {
    if (mediaType === 'image' && !isPdf) {
      setZoomLevel(prev => Math.min(prev + 0.25, 3));
    }
  };

  const handleZoomOut = () => {
    if (mediaType === 'image' && !isPdf) {
      setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
    }
  };

  const handleShare = () => {
    setIsShareModalOpen(true);
  };

  const handleDiaryClick = () => {
    setIsDiarySectionOpen(!isDiarySectionOpen);
    if (!isDiarySectionOpen) {
      setIsCommentSectionOpen(false);
    }
  };

  const handleCommentClick = () => {
    setIsCommentSectionOpen(!isCommentSectionOpen);
    if (!isCommentSectionOpen) {
      setIsDiarySectionOpen(false);
    }
  };

  const handleInfo = () => {
    setIsInfoSidebarOpen(true);
  };

  const handleStar = () => {
    handleFavorite();
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteAsset({ id: assetId }).unwrap();

      // Close modal
      setIsDeleteModalOpen(false);

      // Clear selected media from Redux
      dispatch(clearSelectedMedia());

      // Navigate back to photos page
      navigate(routePaths.Photos);
    } catch (error) {
      console.error('Failed to delete asset:', error);
      // Keep modal open so user can try again or cancel
      // Optionally show error toast/alert here
    }
  };

  // Who is this handlers
  const handleFaceBoxClick = (faceIndex: number) => {
    if (activeFaceIndex === faceIndex) {
      setActiveFaceIndex(null);
      setSearchText('');
      setIsNameSelected(false);
      setSelectedPersonId('');
      setIsEditMode(false);
    } else {
      setActiveFaceIndex(faceIndex);
      setSearchText('');
      setIsNameSelected(false);
      setSelectedPersonId('');
      setIsEditMode(false);
    }
  };

  const handleEditClick = (faceIndex: number, currentName?: string) => {
    const face = mergedFaceDetections[faceIndex];

    setActiveFaceIndex(faceIndex);
    setSearchText(face?.personName || currentName || '');
    setIsNameSelected(false);
    setSelectedPersonId('');
    setIsEditMode(true);
  };

  const [AddFamily] = useAddPeopleMutation();
  const [UpdatePeople] = useUpdatePeopleMutation();
  const [TagPeople] = useAddPeopleAssetMutation();

  const handleDoneClick = async (faceIndex: number) => {
    const nameToUse = searchText.trim();

    if (!nameToUse) return;

    const face = mergedFaceDetections[faceIndex];
    const sourceId = face?.peopleId;
    if (isEditMode && isNameSelected && selectedPersonId && sourceId) {
      try {
        const dataObj = {
          mergePeople: {
            sourcePersonId: sourceId,
            targetPersonId: selectedPersonId,
            deleteSource: false
          }
        };
        await MergeMutation(dataObj).unwrap();
        // Refresh data
        refetchAsset();
        refetchAssetPeople();

        setActiveFaceIndex(null);
        setSearchText('');
        setIsNameSelected(false);
        setSelectedPersonId('');
        setIsEditMode(false);

        showToast({
          type: 'success',
          isAlert: true,
          alertProps: {
            type: 'success',
            message: 'Person tagged successfully!'
          }
        });
      } catch (err) {
        console.log('Error tagging person:', err);
        showToast({
          type: 'error',
          isAlert: true,
          alertProps: {
            type: 'error',
            message: 'Failed to tag person.'
          }
        });
      }

      return;
    }
    if (!isEditMode && isNameSelected && selectedPersonId && sourceId) {
      // Get selected person's data from familySearchOptions
      const selectedPerson = familySearchOptions.find(
        (person: any) => person.id === selectedPersonId
      );

      // Check if face belongs to current user and use their profile image
      let currentPersonImage = face?.profileImage || '';
      if (
        face?.userId === userId &&
        !currentPersonImage &&
        userDetails.profileImageUrl
      ) {
        currentPersonImage = userDetails.profileImageUrl;
      }

      // Set merge data
      setSourcePersonIdForMerge(sourceId);
      setSelectedMergePersonId(selectedPersonId);
      setName(nameToUse);
      setSelectedMergePerson(selectedPerson?.name || nameToUse);
      setNewPersonImage(currentPersonImage);
      setSelectedPersonImage(selectedPerson?.profilePicUrl || '');

      // Show merge modal
      setIsMergePersonOpen(true);

      return;
    }
    if (sourceId && !isNameSelected && !isEditMode) {
      const dataObj = {
        id: sourceId,
        updatePeople: {
          name: nameToUse
        }
      };

      UpdatePeople(dataObj)
        .then((res: any) => {
          if (res?.data) {
            setName(nameToUse);
            setCreatedPersonId(sourceId);
            refetchAsset();
            refetchAssetPeople();
            setIsSelectFriendFamily(true);
          } else {
            console.log('Failed to update name');
          }
        })
        .catch(err => {
          console.log('Error updating name:', err);
        });
    } else if (!isNameSelected && isEditMode) {
      // In edit mode, typing new names is not supported - only selection from dropdown
      console.log('Edit mode: Please select a person from the dropdown');
    } else if (!isNameSelected && face?.userId) {
      const dataObj: any = {
        createPeople: {
          userId: face?.userId,
          type: null,
          name: nameToUse
        }
      };
      const imageFile = await extractFaceFromImage(
        mediaSrc,
        face.boundingBox,
        `profile_${assetId}.jpg`
      );

      try {
        const imageUrl = await uploadToS3(
          imageFile,
          idToken,
          userId,
          'profile',
          '',
          {
            userId: face?.userId
          }
        );
        if (imageUrl) {
          dataObj.createPeople.profileImage = imageUrl;
        }
      } catch (err) {
        console.log('Error uploading face image:', err);
      }

      AddFamily(dataObj)
        .then(async (res: any) => {
          if (res?.data) {
            const peopleId = res?.data?.data?.id;
            try {
              await TagPeople({
                createPeopleAsset: { assetId, peopleId }
              }).unwrap();
            } catch (error) {
              console.error('Error tagging person to asset:', error);
            }
            setName(nameToUse);
            setCreatedPersonId(peopleId);
            refetchAsset();
            refetchAssetPeople();
            setIsSelectFriendFamily(true);
          } else {
            console.log('Failed to update name');
          }
        })
        .catch(err => {
          console.log('Error updating name:', err);
        });
    }
  };

  const handleMergePerson = async () => {
    try {
      const dataObj = {
        mergePeople: {
          sourcePersonId: sourcePersonIdForMerge,
          targetPersonId: selectedMergePersonId,
          deleteSource: true
        }
      };

      await MergeMutation(dataObj).unwrap();

      // Close merge modal
      setIsMergePersonOpen(false);

      // Refresh data
      refetchAsset();
      refetchAssetPeople();

      // Reset states
      setActiveFaceIndex(null);
      setSearchText('');
      setIsNameSelected(false);
      setSelectedPersonId('');
      setIsEditMode(false);
      setSourcePersonIdForMerge('');
      setSelectedMergePersonId('');
      setSelectedMergePerson('');
      setNewPersonImage('');
      setSelectedPersonImage('');

      // Show success message
      showToast({
        type: 'success',
        isAlert: true,
        alertProps: {
          type: 'success',
          message: 'People merged successfully!'
        }
      });
    } catch (err) {
      console.log('Error merging people:', err);
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: 'Failed to merge people.'
        }
      });
    }
  };

  const handleFriendFamilyConfirm = (type: 'family' | 'friend') => {
    setSelectedPersonType(type);
    setIsSelectFriendFamily(false);
    if (type === 'family') {
      setIsOpenFamilyForm(true);
    } else if (type === 'friend') {
      setIsOpenFriendsForm(true);
    }
  };

  const peopleFiltersHandler = () => {
    // Refetch asset data to update face detections with newly identified people
    refetchAsset();
    refetchAssetPeople();
  };

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const imgEl = e.currentTarget;
      // Get image element and its dimensions
      let scaleX = 1,
        scaleY = 1,
        offsetLeft = 0,
        offsetTop = 0;
      let renderedW = 1,
        renderedH = 1,
        naturalW = 1,
        naturalH = 1;
      if (imgEl) {
        renderedW = imgEl.clientWidth;
        renderedH = imgEl.clientHeight;
        naturalW = imgEl.naturalWidth;
        naturalH = imgEl.naturalHeight;
        scaleX = renderedW / naturalW;
        scaleY = renderedH / naturalH;
        // If image is centered, get offset
        const rect = imgEl.getBoundingClientRect();
        const parentRect = imgEl.parentElement?.getBoundingClientRect();
        if (parentRect) {
          offsetLeft = rect.left - parentRect.left;
          offsetTop = rect.top - parentRect.top;
        }
      }
      setImageDimensions({
        scaleX,
        scaleY,
        offsetLeft,
        offsetTop,
        renderedW,
        renderedH,
        naturalW,
        naturalH
      });
    },
    []
  );

  if (isLoading) {
    return <AppLoader isLoading={isLoading} />;
  }

  return (
    <div className="min-h-screen bg-[var(--media-page-bg)] text-gray-100">
      <MediaPostHeader
        onBack={handleBack}
        onExpand={handleExpand}
        onShare={handleShare}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onInfo={handleInfo}
        onStar={handleStar}
        onDelete={handleDeleteClick}
        onDownload={() => handleDownloads(mediaSrc)}
        mediaType={mediaType as MediaType}
        isFavorite={isFavorite}
        canShare={canShare}
        canDelete={canDelete}
        canDownload={canDownload}
        isPrivate={assetData?.data?.isPrivate}
      />

      <main className="max-w-6xl mx-auto px-6 pb-10">
        <section className="bg-[var(--media-comment-bg)] rounded-md shadow-lg">
          <div className="relative bg-media-input">
            <div
              className={`w-full h-auto ${mediaType === 'video' ? 'min-h-[300px]' : ''} bg-media-input flex items-center justify-center`}>
              {isPdf ? (
                <div className="w-full h-[80vh] flex justify-center bg-black overflow-y-auto">
                  <div className="w-full h-full max-w-5xl bg-white rounded-lg overflow-y-auto shadow-xl">
                    <DocViewer
                      documents={[
                        {
                          uri: mediaSrc,
                          fileType: 'pdf'
                        }
                      ]}
                      pluginRenderers={DocViewerRenderers}
                      config={{
                        header: {
                          disableHeader: true,
                          disableFileName: true
                        },
                        pdfZoom: {
                          defaultZoom: 1.0,
                          zoomJump: 0.2
                        }
                      }}
                      style={{
                        width: '100%',
                        height: '100%'
                      }}
                    />
                  </div>
                </div>
              ) : isAudio ? (
                <div className="w-full flex items-center justify-center py-20 bg-black">
                  <audio src={mediaSrc} controls className="w-full max-w-2xl" />
                </div>
              ) : (
                <div
                  className="media-zoom-container"
                  style={{
                    transform:
                      mediaType === 'image' ? `scale(${zoomLevel})` : 'none'
                  }}>
                  <MediaPlayer
                    type={mediaType}
                    src={mediaSrc}
                    controls={true}
                    muted={true}
                    width="100%"
                    height="auto"
                    containerClassName="w-full h-auto block"
                    onImageLoad={onImageLoad}
                  />
                  {/* Face bounding boxes overlay */}
                  {mediaType === 'image' && mergedFaceDetections.length > 0 && (
                    <div className="media-face-overlay">
                      {mergedFaceDetections.map((face, idx) => {
                        const box = face.boundingBox;
                        const isActive = activeFaceIndex === idx;
                        const isHovered = hoveredFaceIndex === idx;

                        // AWS Rekognition bounding box values are ratios (0-1), convert to pixel values
                        const boxPx = {
                          left: (box.left || 0) * imageDimensions?.naturalW,
                          top: (box.top || 0) * imageDimensions?.naturalH,
                          width: (box.width || 0) * imageDimensions?.naturalW,
                          height: (box.height || 0) * imageDimensions?.naturalH
                        };

                        // Map bounding box to rendered image
                        const diameter =
                          Math.max(boxPx.width, boxPx.height) *
                          imageDimensions?.scaleX;
                        const centerX =
                          (boxPx.left + boxPx.width / 2) *
                            imageDimensions?.scaleX +
                          imageDimensions?.offsetLeft;
                        const centerY =
                          (boxPx.top + boxPx.height / 2) *
                            imageDimensions?.scaleY +
                          imageDimensions?.offsetTop;
                        const circleLeft = centerX - diameter / 2;
                        const circleTop = centerY - diameter / 2;

                        return (
                          <div
                            key={idx}
                            ref={isActive || isHovered ? facePopupRef : null}
                            onClick={e => e.stopPropagation()}
                            onMouseEnter={() => setHoveredFaceIndex(idx)}
                            onMouseLeave={() => {
                              if (activeFaceIndex !== idx) {
                                setHoveredFaceIndex(null);
                              }
                            }}>
                            {/* Circular Bounding Box */}
                            <div
                              className="media-face-box"
                              style={{
                                left: circleLeft + 'px',
                                top: circleTop + 'px',
                                width: diameter + 'px',
                                aspectRatio: '1 / 1',
                                cursor: 'pointer',
                                borderColor: isActive
                                  ? 'white'
                                  : isHovered
                                    ? 'white'
                                    : 'transparent',
                                borderWidth:
                                  isHovered || isActive ? '2px' : '0px',
                                borderRadius: '50%'
                              }}
                              onClick={e => {
                                e.stopPropagation();
                                if (face.personName === null) {
                                  // Toggle input for unidentified faces
                                  handleFaceBoxClick(idx);
                                } else {
                                  // Open edit mode for identified faces
                                  if (!isActive) {
                                    handleEditClick(idx, face.personName || '');
                                  }
                                }
                              }}
                            />

                            {/* Show label when hovered or active */}
                            {(isHovered || isActive) && (
                              <div
                                style={{
                                  position: 'absolute',
                                  left: circleLeft + diameter + 'px',
                                  top: centerY + 'px',
                                  transform: 'translate(5px, -50%)',
                                  pointerEvents: 'auto',
                                  zIndex: 20
                                }}>
                                {face.personName === null ? (
                                  <div className="flex flex-col gap-2 bg-white rounded-lg shadow-lg px-3 py-2 border border-gray-200">
                                    {/* Who is this - for unidentified faces */}
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-semibold text-[#49712B] whitespace-nowrap">
                                        {t('whoIsThis')}
                                      </span>
                                      <div
                                        onClick={e => {
                                          e.stopPropagation();
                                          handleFaceBoxClick(idx);
                                        }}
                                        className="w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer text-[#49712B] border-[#49712B] hover:bg-[#F9FEE9] transition-colors">
                                        <span className="text-sm font-bold">
                                          {isActive ? '-' : '+'}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Input field when active */}
                                    {isActive && (
                                      <div className="flex items-center gap-2">
                                        <div className="w-[180px] media-sidebar-select-fix">
                                          <SearchableSelectInput
                                            name="name"
                                            placeholder="Enter name"
                                            isWhiteTheme
                                            isFromCreatePeople={true}
                                            value={searchText}
                                            options={familySearchOptions}
                                            onSearch={(value: string) => {
                                              setSearchText(value);
                                              setIsNameSelected(false);
                                              setSelectedPersonId('');
                                            }}
                                            onChange={(selectedOption: any) => {
                                              setSearchText(
                                                selectedOption?.name || ''
                                              );

                                              // Only update if selecting from dropdown (has id)
                                              if (selectedOption?.id) {
                                                setIsNameSelected(true);
                                                setSelectedPersonId(
                                                  selectedOption?.id || ''
                                                );
                                              } else {
                                                // User is just typing
                                                setIsNameSelected(false);
                                                setSelectedPersonId('');
                                              }
                                            }}
                                          />
                                        </div>
                                        <Button
                                          btnStyle="link"
                                          type="button"
                                          onClick={e => {
                                            e.stopPropagation();
                                            handleDoneClick(idx);
                                          }}
                                          className="px-3 py-2 !bg-[#F9FEE9] text-[var(--na-primary-color)] font-medium rounded-md text-sm border border-[#DCDCDC] hover:!bg-[#7F9D48] hover:!text-white">
                                          {t('done')}
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <>
                                    {/* Show person name for identified faces */}
                                    {!isActive ? (
                                      <div className="flex items-center gap-2 bg-white rounded-lg shadow-lg px-3 py-2 border border-gray-200">
                                        {face.profileImage && (
                                          <img
                                            src={face.profileImage}
                                            alt={face.personName}
                                            className="w-6 h-6 rounded-full object-cover"
                                          />
                                        )}
                                        <span className="text-sm font-semibold text-[#353535] whitespace-nowrap">
                                          {face.personName}
                                        </span>
                                        <div
                                          onClick={e => {
                                            e.stopPropagation();
                                            handleEditClick(
                                              idx,
                                              face.personName || ''
                                            );
                                          }}
                                          className="w-5 h-5 flex items-center justify-center cursor-pointer text-[#353535] hover:text-[#7F9D48] transition-colors">
                                          <SVGAssets.EditIcon className="w-4 h-4" />
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex flex-col gap-2 bg-white rounded-lg shadow-lg px-3 py-2 border border-gray-200">
                                        {/* Edit mode for identified faces */}
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-semibold text-[#49712B] whitespace-nowrap">
                                            {t('tagPeople')}
                                          </span>
                                          <div
                                            onClick={e => {
                                              e.stopPropagation();
                                              handleFaceBoxClick(idx);
                                            }}
                                            className="w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer text-[#49712B] border-[#49712B] hover:bg-[#F9FEE9] transition-colors">
                                            <span className="text-sm font-bold">
                                              -
                                            </span>
                                          </div>
                                        </div>

                                        {/* Input field when editing */}
                                        <div className="flex items-center gap-2">
                                          <div className="w-[180px] media-sidebar-select-fix">
                                            <SearchableSelectInput
                                              name="name"
                                              placeholder="Enter name"
                                              isWhiteTheme
                                              isFromCreatePeople={true}
                                              value={searchText}
                                              options={familySearchOptions}
                                              onSearch={(value: string) => {
                                                setSearchText(value);
                                                setIsNameSelected(false);
                                                setSelectedPersonId('');
                                              }}
                                              onChange={(
                                                selectedOption: any
                                              ) => {
                                                console.log(
                                                  'Selected option (edit mode):',
                                                  selectedOption
                                                );
                                                setSearchText(
                                                  selectedOption?.name || ''
                                                );

                                                // In edit mode: only update if user selects from dropdown
                                                if (selectedOption?.id) {
                                                  // User selected from dropdown
                                                  setIsNameSelected(true);
                                                  setSelectedPersonId(
                                                    selectedOption?.id || ''
                                                  );
                                                } else {
                                                  // User is just typing
                                                  setIsNameSelected(false);
                                                  setSelectedPersonId('');
                                                }
                                              }}
                                            />
                                          </div>
                                          <Button
                                            btnStyle="link"
                                            type="button"
                                            onClick={e => {
                                              e.stopPropagation();
                                              handleDoneClick(idx);
                                            }}
                                            className="px-3 py-2 !bg-[#F9FEE9] text-[var(--na-primary-color)] font-medium rounded-md text-sm border border-[#DCDCDC] hover:!bg-[#7F9D48] hover:!text-white">
                                            {t('done')}
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <MediaStats
            likes={optimisticLikeCount}
            comments={optimisticCommentCount}
            shares={optimisticDiaryCount}
            isLiked={isLiked}
            onLike={handleLike}
            onComment={handleCommentClick}
            onShare={handleDiaryClick}
          />
        </section>
        {isCommentSectionOpen && (
          <CommentSection
            comments={displayComments}
            currentUserId={userId}
            onAddComment={handleAddComment}
            onUpdateComment={handleUpdateComment}
            onDeleteComment={handleDeleteComment}
            onCommentLikeUpdate={handleCommentLikeUpdate}
          />
        )}

        {isDiarySectionOpen && (
          <DiarySection
            assetId={assetId}
            isOpen={isDiarySectionOpen}
            onDiaryUpdate={handleDiaryUpdate}
          />
        )}
      </main>

      {/* Full Screen Modal */}
      <FullScreenModal
        isOpen={isFullScreenOpen}
        onClose={() => setIsFullScreenOpen(false)}
        mediaType={mediaType}
        mediaSrc={mediaSrc}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        variant="delete"
      />

      {/* Share Modal */}
      <ShareModalComponent
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        link={mediaSrc || ''}
        assetId={assetId}
        mode="share"
        shareType="asset"
        onMessage={(type, message) =>
          showToast({ type, isAlert: true, alertProps: { type, message } })
        }
      />

      {/* Info Sidebar */}
      <MediaInfoSidebar
        isOpen={isInfoSidebarOpen}
        onClose={() => setIsInfoSidebarOpen(false)}
        onReopen={() => setIsInfoSidebarOpen(true)}
        assetId={assetId}
      />

      {/* Who is this Modals */}
      <SelectFriendFamily
        isOpen={isSelectFriendFamily}
        onClose={() => {
          setIsSelectFriendFamily(false);
          setName('');
          setCreatedPersonId('');
          setActiveFaceIndex(null);
          setSearchText('');
          setIsNameSelected(false);
          setSelectedPersonId('');
          setIsEditMode(false);
        }}
        onConfirm={handleFriendFamilyConfirm}
        personName={name}
      />

      <FriendsForm
        isOpen={isOpenFriendsForm}
        onClose={() => {
          setIsOpenFriendsForm(false);
          setName('');
          setCreatedPersonId('');
          setActiveFaceIndex(null);
          setSearchText('');
          setIsNameSelected(false);
          setSelectedPersonId('');
          setIsEditMode(false);
          refetchAsset();
          refetchAssetPeople();
        }}
        name={name}
        selectedPersonType={selectedPersonType}
        peopleFiltersHandler={peopleFiltersHandler}
        createdPersonId={createdPersonId}
        personToMove={null}
        setIsLoading={setIsLoading}
      />

      <FamilyForm
        isOpen={isOpenFamilyForm}
        onClose={() => {
          setIsOpenFamilyForm(false);
          setName('');
          setCreatedPersonId('');
          setActiveFaceIndex(null);
          setSearchText('');
          setIsNameSelected(false);
          setSelectedPersonId('');
          setIsEditMode(false);
          refetchAsset();
          refetchAssetPeople();
        }}
        name={name}
        selectedPersonType={selectedPersonType}
        peopleFiltersHandler={peopleFiltersHandler}
        personToMove={null}
        createdPersonId={createdPersonId}
        ownerId={ownerId}
        setIsLoading={setIsLoading}
      />

      {/* Merge Person Modal */}
      <MergePerson
        isOpen={isMergePersonOpen}
        onClose={() => {
          setIsMergePersonOpen(false);
          setSourcePersonIdForMerge('');
          setSelectedMergePersonId('');
          setSelectedMergePerson('');
          setNewPersonImage('');
          setSelectedPersonImage('');
        }}
        onMerge={handleMergePerson}
        personName={name}
        selectedPersonName={selectedMergePerson}
        newPersonImage={newPersonImage}
        selectedPersonImage={selectedPersonImage}
      />
    </div>
  );
};

export default MediaPostScreen;
